/**
 * Body of one Catalog tab. Always renders TWO sections — Available now +
 * Coming soon — each with its own search bar that narrows just that
 * section. Per Ean 2026-06-09: the prior single-section-per-tab buried the
 * Available-vs-Coming distinction; users now scan two clear shelves.
 *
 * Generic over tile type so the same shell powers Connectors / Knowledge /
 * Models. Caller provides `renderTile(item, { comingSoon })` so each pillar
 * keeps its own tile shape (logos for connectors, providers for models,
 * categories for knowledge) without forcing a one-size-fits-all card.
 */

import { useState } from 'react';
import type React from 'react';

import { CatalogSection } from './CatalogSection.tsx';

export function CatalogTabBody<T extends { id: string; display_name: string }>({
  available,
  comingSoon,
  pillarLabel,
  renderTile,
  matchesQuery,
}: {
  available: T[];
  comingSoon: T[];
  pillarLabel: string;
  renderTile: (item: T, opts: { comingSoon: boolean }) => React.ReactNode;
  matchesQuery: (item: T, query: string) => boolean;
}) {
  const [availSearch, setAvailSearch] = useState('');
  const [soonSearch, setSoonSearch] = useState('');

  const filterFn = (items: T[], q: string): T[] => {
    const trimmed = q.trim().toLowerCase();
    if (trimmed === '') return items;
    return items.filter((i) => matchesQuery(i, trimmed));
  };
  const availFiltered = filterFn(available, availSearch);
  const soonFiltered = filterFn(comingSoon, soonSearch);

  return (
    <div className="lightning-section">
      {/* Available section. always rendered, even when empty, so users
         see the section header + count even before anything is installable. */}
      <CatalogSection
        title="Available now"
        count={available.length}
        search={availSearch}
        onSearchChange={setAvailSearch}
        placeholder={`Search available ${pillarLabel}…`}
        emptyAll={`No ${pillarLabel} available yet. Check Preview below or suggest one to Olytic.`}
        emptyFiltered={(q) => `Nothing in available matches "${q}".`}
        items={availFiltered}
        renderTile={(item) => renderTile(item, { comingSoon: false })}
      />

      {/* Coming soon section. same shape, dimmer visual hint via the
         sub-header so the eye scans Available first. */}
      <div style={{ marginTop: '32px' }}>
        <CatalogSection
          title="Preview"
          count={comingSoon.length}
          search={soonSearch}
          onSearchChange={setSoonSearch}
          placeholder={`Search preview ${pillarLabel}…`}
          emptyAll={`No upcoming ${pillarLabel} announced yet. Check back as the roadmap evolves.`}
          emptyFiltered={(q) => `Nothing in preview matches "${q}".`}
          items={soonFiltered}
          renderTile={(item) => renderTile(item, { comingSoon: true })}
        />
      </div>
    </div>
  );
}
